/**
 * The pure side of one section's settings editor: the fields' values (what
 * is typed over what is saved), the changes worth sending, why they cannot
 * be saved, and the defaults "Back to defaults" puts in the fields.
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  DROP_GEN_ENGINE_DEFAULTS,
  DROP_GEN_ENGINE_SETTINGS,
  type DropGenEngineSettingDef,
  type DropGenEngineSettingKey,
  type DropGenEngineSettings,
  engineSettingProblem,
} from '@trafficpulse/shared';

export type SettingsDraft = Partial<Record<DropGenEngineSettingKey, number>>;

export const defsOf = (keys: readonly DropGenEngineSettingKey[]): DropGenEngineSettingDef[] =>
  keys.flatMap((key) => DROP_GEN_ENGINE_SETTINGS.filter((d) => d.key === key));

export const valueOf = (draft: SettingsDraft, saved: DropGenEngineSettings, key: DropGenEngineSettingKey): number =>
  draft[key] ?? saved[key];

/** Only the values that differ from what is saved — the PUT body. */
export function changedPatch(draft: SettingsDraft, saved: DropGenEngineSettings, keys: readonly DropGenEngineSettingKey[]): SettingsDraft {
  const out: SettingsDraft = {};
  for (const key of keys) if (draft[key] !== undefined && draft[key] !== saved[key]) out[key] = draft[key];
  return out;
}

/** Why the changes cannot be saved, one line each; empty = they can. */
export const problemsOf = (patch: SettingsDraft): string[] =>
  Object.entries(patch).flatMap(([key, value]) => {
    const problem = engineSettingProblem(key, value);
    return problem ? [problem] : [];
  });

export function defaultsDraft(keys: readonly DropGenEngineSettingKey[]): SettingsDraft {
  const out: SettingsDraft = {};
  for (const key of keys) out[key] = DROP_GEN_ENGINE_DEFAULTS[key];
  return out;
}

const UNIT_WORDS: Record<DropGenEngineSettingDef['unit'], string> = {
  min: 'min',
  h: 'h',
  days: 'days',
  ms: 'ms',
  drafts: 'drafts',
  reads: 'reads',
  tries: 'tries',
  calls: 'calls',
};

export const unitWords = (def: DropGenEngineSettingDef): string => UNIT_WORDS[def.unit];

/** 1500 ms → "1.5 s" — for the prose; the field keeps milliseconds. */
export const seconds = (ms: number): string => `${Number((ms / 1000).toFixed(2))} s`;
